import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { CheckCircle2, ShieldCheck, Banknote, ArrowRight, Loader2, FileCheck2, RefreshCcw, Globe } from 'lucide-react';
import {
  stripeConnectMockComplete,
  stripeConnectOnboard,
  stripeConnectRefresh,
  stripeConnectListCountries,
  stripeConnectResetOnboarding,
} from '../api/phase7';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useLocation, useNavigate } from 'react-router-dom';

// Phase 36.2 — labels for the country selector. The AUTHORITATIVE list
// comes from GET /api/payouts/countries; this map only provides the
// human-readable label. Any code missing here still renders (just as
// its ISO code) so a Stripe expansion doesn't break the UI.
const COUNTRY_LABELS = {
  AE: 'United Arab Emirates', AT: 'Austria', AU: 'Australia', BE: 'Belgium',
  BG: 'Bulgaria', BR: 'Brazil', CA: 'Canada', CH: 'Switzerland',
  CI: "Côte d'Ivoire", CO: 'Colombia', CR: 'Costa Rica', CY: 'Cyprus',
  CZ: 'Czech Republic', DE: 'Germany', DK: 'Denmark', EE: 'Estonia',
  ES: 'Spain', FI: 'Finland', FR: 'France', GB: 'United Kingdom',
  GH: 'Ghana', GI: 'Gibraltar', GR: 'Greece', HK: 'Hong Kong',
  HR: 'Croatia', HU: 'Hungary', ID: 'Indonesia', IE: 'Ireland',
  IN: 'India', IS: 'Iceland', IT: 'Italy', JP: 'Japan',
  KE: 'Kenya', LI: 'Liechtenstein', LT: 'Lithuania', LU: 'Luxembourg',
  LV: 'Latvia', MT: 'Malta', MX: 'Mexico', MY: 'Malaysia',
  NG: 'Nigeria', NL: 'Netherlands', NO: 'Norway', NZ: 'New Zealand',
  PE: 'Peru', PH: 'Philippines', PL: 'Poland', PT: 'Portugal',
  RO: 'Romania', SA: 'Saudi Arabia', SE: 'Sweden', SG: 'Singapore',
  SI: 'Slovenia', SK: 'Slovakia', SN: 'Senegal', TH: 'Thailand',
  TR: 'Turkey', US: 'United States', UY: 'Uruguay', VN: 'Vietnam',
  ZA: 'South Africa',
};

/**
 * PayoutOnboardingCard — single-step creator onboarding using
 * Stripe Connect Express. Stripe handles identity verification (KYC),
 * payout method, tax info and bank account capture in one hosted flow.
 *
 *  - Locked (initial): user has no Stripe Connect account yet
 *  - In progress: account created, awaiting Stripe onboarding completion
 *  - Ready: payouts_enabled = true (Stripe Express verified the creator)
 */
const PayoutOnboardingCard = () => {
  const { t } = useTranslation('lives');
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // Phase 36.2 — country of residence selected by the creator before
  // Stripe creates the connected account. Default = existing stripe_country
  // if the user already has one (resume flow); otherwise empty.
  const [country, setCountry] = useState(user?.stripe_country || '');
  const [countryList, setCountryList] = useState([]);
  const [resetBusy, setResetBusy] = useState(false);

  const hasAccount = !!user?.stripe_connect_id;
  const allReady = !!user?.is_payout_ready;
  const inProgress = hasAccount && !allReady;

  // Load supported countries once.
  useEffect(() => {
    let cancelled = false;
    stripeConnectListCountries().then((codes) => {
      if (!cancelled) setCountryList(codes);
    }).catch(() => { /* soft-fail */ });
    return () => { cancelled = true; };
  }, []);

  // Detect Stripe Connect return URL params (mock or real)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeReturn = params.get('stripe_connect');
    const mockAccount = params.get('mock_account');
    if (!stripeReturn && !mockAccount) return;

    const completeMock = async () => {
      try {
        await stripeConnectMockComplete();
        await refresh();
        toast({ title: 'Stripe Connect linked', description: 'Account verified (mock mode).' });
      } catch (e) {
        toast({
          title: 'Could not finalise onboarding',
          description: e.response?.data?.detail || 'Try again',
          variant: 'destructive',
        });
      }
    };

    const refreshReal = async () => {
      try {
        await stripeConnectRefresh();
        await refresh();
      } catch (_e) {
        // Soft fail
      }
    };

    if (mockAccount) {
      completeMock();
    } else if (stripeReturn === 'return') {
      refreshReal();
    }
    // Clean params from URL
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleStripeOnboard = async () => {
    if (!country) {
      toast({ title: 'Country required', description: 'Select your country of residence to continue.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const data = await stripeConnectOnboard(country);
      if (data.onboarding_url) {
        // For mock URLs pointing back to this app, navigate in-app.
        try {
          const url = new URL(data.onboarding_url);
          if (url.origin === window.location.origin) {
            navigate(url.pathname + url.search);
            return;
          }
        } catch (_) { /* ignore parse errors */ }
        window.location.href = data.onboarding_url;
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Try again';
      // Special-case the country_mismatch 409 so we prompt to reset.
      if (typeof detail === 'string' && detail.startsWith('country_mismatch')) {
        toast({
          title: 'Country mismatch',
          description: 'This Stripe account is locked to a different country. Reset onboarding to switch.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Could not start Stripe Connect', description: detail, variant: 'destructive' });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(
      'Reset your Stripe onboarding? This detaches the current Stripe account so you can restart with a different country. The old Stripe account cannot be reused.'
    )) return;
    setResetBusy(true);
    try {
      await stripeConnectResetOnboarding();
      await refresh();
      toast({ title: 'Stripe onboarding reset', description: 'You can now restart with the correct country.' });
    } catch (e) {
      toast({ title: 'Reset failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setResetBusy(false);
    }
  };

  if (allReady) {
    return (
      <div
        className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-5"
        data-testid="payout-ready-banner"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/20">
              <Banknote className="h-5 w-5 text-emerald-300" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{t('vip_panel.payout_onboarding.ready_title')}</p>
              <p className="text-xs text-muted-foreground">
                {t('vip_panel.payout_onboarding.ready_desc')}
                {user?.stripe_country && (
                  <> · <span className="font-mono">{user.stripe_country}</span></>
                )}
              </p>
            </div>
          </div>
          <span className="rounded-full bg-emerald-500/20 px-2.5 py-1 text-[11px] font-semibold uppercase text-emerald-300">
            {t('vip_panel.payout_onboarding.ready_badge')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-border bg-card p-5"
      data-testid="payout-onboarding-card"
    >
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 gold-text" />
        <h2 className="text-sm font-semibold uppercase tracking-wide">{t('vip_panel.payout_onboarding.title')}</h2>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        {t('vip_panel.payout_onboarding.desc')}
      </p>

      <ul className="mb-4 space-y-1.5 text-xs text-foreground">
        <li className="flex items-center gap-2"><FileCheck2 className="h-3.5 w-3.5 gold-text" />{t('vip_panel.payout_onboarding.bullet_id')}</li>
        <li className="flex items-center gap-2"><FileCheck2 className="h-3.5 w-3.5 gold-text" />{t('vip_panel.payout_onboarding.bullet_tax')}</li>
        <li className="flex items-center gap-2"><FileCheck2 className="h-3.5 w-3.5 gold-text" />{t('vip_panel.payout_onboarding.bullet_bank')}</li>
      </ul>

      {/* Phase 36.2 — country of residence selector */}
      <div className="mb-4">
        <label htmlFor="stripe-country" className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground">
          <Globe className="h-3.5 w-3.5 gold-text" /> {t('vip_panel.payout_onboarding.country_label')}
        </label>
        <select
          id="stripe-country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          disabled={hasAccount}
          className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="stripe-country-select"
        >
          <option value="">{t('vip_panel.payout_onboarding.country_placeholder')}</option>
          {countryList.map((code) => (
            <option key={code} value={code}>
              {code} — {COUNTRY_LABELS[code] || code}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {hasAccount
            ? t('vip_panel.payout_onboarding.country_hint_locked')
            : t('vip_panel.payout_onboarding.country_hint_new')}
        </p>
      </div>

      <div className="flex items-center justify-between gap-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-xs font-semibold gold-text">
            {inProgress ? <Loader2 className="h-4 w-4 animate-spin" /> : '1'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              {inProgress ? t('vip_panel.payout_onboarding.finish_stripe') : t('vip_panel.payout_onboarding.connect_stripe')}
            </p>
            <p className="text-xs text-muted-foreground">
              {inProgress
                ? t('vip_panel.payout_onboarding.resume_desc')
                : t('vip_panel.payout_onboarding.single_step')}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          className="gold-gradient"
          disabled={busy || !country}
          onClick={handleStripeOnboard}
          data-testid="open-stripe-onboard-btn"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
            <>{inProgress ? t('vip_panel.payout_onboarding.cta_resume') : t('vip_panel.payout_onboarding.cta_connect')} <ArrowRight className="ml-1 h-3.5 w-3.5" /></>
          )}
        </Button>
      </div>

      {hasAccount && (
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground">
            {t('vip_panel.payout_onboarding.account_on_file')}: <code className="rounded bg-muted px-1.5 py-0.5">{user.stripe_connect_id}</code>
            {user?.stripe_country && (
              <> · {t('vip_panel.payout_onboarding.country_field')}: <code className="rounded bg-muted px-1.5 py-0.5">{user.stripe_country}</code></>
            )}
          </p>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleReset}
            disabled={resetBusy}
            className="h-7 text-[11px]"
            data-testid="stripe-onboard-reset-btn"
          >
            {resetBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="mr-1 h-3 w-3" />}
            {t('vip_panel.payout_onboarding.reset')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PayoutOnboardingCard;
